/**
 * Theme contract.
 *
 * A theme is a pair: a Mantine config (the bits Mantine owns — primaryColor,
 * colors, fonts, component defaults) and a flat map of CSS custom properties
 * (the bits Mantine can't see — glow, panel opacity, content scale, motion).
 * The resolver writes the cssVars onto the document root and feeds the mantine
 * config to MantineProvider.
 *
 * Built-in themes (this folder) and admin-authored custom themes (backend
 * `themes.config` jsonb) both flow through normalizeThemeConfig() so the
 * resolver treats them identically — the only difference is one is code and
 * the other is fetched.
 *
 * @typedef {Object} ThemeDescriptor
 * @property {string}  id          stable key stored in story.options.theme
 * @property {string}  name        display name
 * @property {boolean} assignable  false hides it from the admin story picker
 *                                  (e.g. highContrast, which is player-only)
 * @property {('dark'|'light')} [colorScheme]  Mantine color scheme; defaults
 *                                  to 'dark'. Light themes (Daylight) set 'light'.
 * @property {Object}  mantine     Mantine createTheme() config
 * @property {Object}  cssVars     --bw-* custom properties (see CSS_VAR_KEYS)
 */

// The full set of --bw-* vars a theme may set. Anything omitted falls back to
// the :root default in index.css (the Novastar baseline), so a partial theme
// (or a sparse custom theme) is always safe.
export const CSS_VAR_KEYS = [
  '--bw-bg',
  '--bw-body-color',
  '--bw-panel-bg',
  '--bw-panel-border',
  '--bw-accent',
  '--bw-glow-color',
  '--bw-glow-text-color',
  '--bw-body-font',
  '--bw-heading-font',
  '--bw-heading-letter-spacing',
  '--bw-content-base',
]

/**
 * Coerce a raw theme config (built-in OR a backend custom theme's `config`
 * jsonb) into a safe ThemeDescriptor. Drops unknown cssVars keys so a
 * malformed/hostile custom config can't inject arbitrary CSS properties, and
 * guarantees the mantine + cssVars objects exist.
 *
 * @param {Object} raw  { id, name, assignable?, mantine?, cssVars? }
 * @returns {ThemeDescriptor}
 */
export function normalizeThemeConfig(raw = {}) {
  const cssVars = {}
  if (raw.cssVars && typeof raw.cssVars === 'object') {
    for (const key of CSS_VAR_KEYS) {
      if (raw.cssVars[key] != null) cssVars[key] = String(raw.cssVars[key])
    }
  }
  return {
    id: raw.id,
    name: raw.name || raw.id,
    assignable: raw.assignable !== false,
    colorScheme: raw.colorScheme === 'light' ? 'light' : 'dark',
    mantine: raw.mantine && typeof raw.mantine === 'object' ? raw.mantine : {},
    cssVars,
  }
}
